import {
    BarElement,
    CategoryScale,
    Chart,
    ChartConfiguration,
    ChartDataset,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title, ChartDataLabels);

export interface StackedVerticalBarChartDataset {
    id?: string;
    label: string;
    values: number[];
    totals?: number[];
    backgroundColor?: string;
    borderColor?: string;
}

export interface StackedVerticalBarChartData {
    labels: string[];
    datasets: StackedVerticalBarChartDataset[];
    totals?: number[];
}

export type StackedVerticalBarChartTooltipLabelFormatter = (payload: {
    label: string;
    value: number;
    dataIndex: number;
    datasetIndex: number;
    dataset: StackedVerticalBarChartDataset;
}) => string;

export type StackedVerticalBarChartTooltipFooterFormatter = (payload: {
    dataIndex: number;
    total?: number;
}) => string | undefined;

export interface StackedVerticalBarChartOptions {
    yAxisMax?: number;
    yAxisSuggestedMax?: number;
    yAxisStepSize?: number;
    formatYAxisTick?: (value: number) => string;
    formatTooltipLabel?: StackedVerticalBarChartTooltipLabelFormatter;
    formatTooltipFooter?: StackedVerticalBarChartTooltipFooterFormatter;
}

type InternalDataset = ChartDataset<'bar'> & {
    __metaTotals?: number[];
};

export const createStackedVerticalBarChart = (
    ctx: CanvasRenderingContext2D | null,
    chartData: StackedVerticalBarChartData,
    options?: StackedVerticalBarChartOptions,
): Chart<'bar'> | undefined => {
    if (!ctx) return undefined;
    if (!chartData.datasets.length) return undefined;

    const preparedDatasets: InternalDataset[] = chartData.datasets.map((dataset) => ({
        type: 'bar',
        label: dataset.label,
        data: dataset.values,
        backgroundColor: dataset.backgroundColor,
        borderColor: dataset.borderColor ?? '#ffffff',
        borderWidth: 1,
        stack: 'stacked',
        barPercentage: 0.95,
        categoryPercentage: 0.95,
        __metaTotals: dataset.totals,
    }));

    const tooltipLabelFormatter = options?.formatTooltipLabel;
    const tooltipFooterFormatter = options?.formatTooltipFooter;
    const dayTotals = chartData.totals;
    const formatYAxisTick = options?.formatYAxisTick ?? ((value: number) => String(value));

    const labelCount = chartData.labels?.length ?? 0;
    const maxVisibleXAxisLabels = 8;
    const labelStep =
        labelCount > maxVisibleXAxisLabels ? Math.ceil(labelCount / maxVisibleXAxisLabels) : 1;

    const config: ChartConfiguration<'bar'> = {
        type: 'bar',
        data: {
            labels: chartData.labels,
            datasets: preparedDatasets,
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'nearest',
                intersect: true,
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => {
                            if (!items.length) return '';
                            const index = items[0].dataIndex ?? 0;
                            const raw = chartData.labels?.[index] ?? '';
                            return String(raw).replace(/\n/g, ' ');
                        },
                        label: (context) => {
                            const rawValue =
                                typeof context.parsed.y === 'number' ? context.parsed.y : 0;
                            const datasetIndex = context.datasetIndex ?? 0;
                            const dataIndex = context.dataIndex ?? 0;
                            const sourceDataset = chartData.datasets[datasetIndex];
                            if (tooltipLabelFormatter) {
                                return tooltipLabelFormatter({
                                    label: sourceDataset?.label ?? '',
                                    value: rawValue,
                                    dataIndex,
                                    datasetIndex,
                                    dataset: sourceDataset ?? {
                                        label: '',
                                        values: [],
                                    },
                                });
                            }
                            const datasetLabel = context.dataset.label ?? '';
                            return `${datasetLabel}: ${rawValue}`;
                        },
                        footer: (items) => {
                            if (!tooltipFooterFormatter || !items.length) {
                                return '';
                            }
                            const index = items[0].dataIndex ?? 0;
                            const total = dayTotals ? dayTotals[index] : undefined;
                            return tooltipFooterFormatter({ dataIndex: index, total }) ?? '';
                        },
                    },
                },
                datalabels: {
                    display: (ctx) => {
                        const value = ctx.dataset.data?.[ctx.dataIndex];
                        return typeof value === 'number' && value > 0;
                    },
                    formatter: (_, ctx) => {
                        const label = chartData.datasets[ctx.datasetIndex]?.label ?? '';
                        if (label.length <= 10) return label;
                        return `${label.slice(0, 9)}…`;
                    },
                    color: '#111827',
                    font: { size: 11, weight: 'bold' },
                    textAlign: 'center',
                    anchor: 'center',
                    align: 'center',
                    clip: true,
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: {
                        display: true,
                        drawOnChartArea: true,
                        drawTicks: false,
                        color: '#e5e7eb',
                        lineWidth: 1,
                    },
                    ticks: {
                        autoSkip: false,
                        maxRotation: 0,
                        minRotation: 0,
                        padding: 6,
                        font: { size: 11 },
                        callback: (value, index) => {
                            void value;
                            const i = index as number;
                            if (labelStep > 1 && i % labelStep !== 0) return '';
                            const raw = chartData.labels[i] ?? '';
                            return String(raw).replace(/\n/g, ' ');
                        },
                    },
                },
                y: {
                    stacked: true,
                    min: 0,
                    suggestedMax: options?.yAxisSuggestedMax,
                    max: options?.yAxisMax,
                    grid: {},
                    ticks: {
                        ...(options?.yAxisStepSize ? { stepSize: options.yAxisStepSize } : {}),
                        maxTicksLimit: 8,
                        padding: 6,
                        callback: (value) => formatYAxisTick(Number(value)),
                    },
                },
            },
        },
    };

    return new Chart(ctx, config);
};
